import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AppButton,
  Box,
  CardTypesEnum,
  CarrierConfigIcon,
  DividerIcon,
  ErrorMessage,
  Inscription,
  Preloader,
  Text,
  TextAreaInput,
  useDebounce,
} from '@common-fe/common-fe';
import dayjs from 'dayjs';

import { DEFAULT_DATE_FORMAT, REQUIRED_TEXT } from '@/common/constants';
import theme from '@/styles/theme';

import { Renewal } from '../../renewals.types';
import HealthPlanSummaryCard from '../components/HealthPlanSummaryCard';
import { validateHealthPlans } from '../components/HealthPlanSummaryCard/hooks/useHealthPlanCodeActions';
import { useRunActiveCodeValidation } from '../hooks';
import { RENEWAL_SETUP_CONTENT_WIDTH } from '../RenewalSetup';
import { RenewalPlanOptionValues, RenewalSetupStep } from '../RenewalSetup.types';
import RenewalSetupLinks from '../RenewalSetupLinks';
import { useHealthPlansStore, useRenewalSetupStore } from '../stores';
import { StepOption } from '../stores/useRenewalSetup.store';
import { getStepLabel, hasAnyFieldError } from '../utils';

import HealthPlansForm from './HealthPlansForm';
import HealthPlansSummary from './HealthPlansSummary';

const MAX_NOTE_LENGTH = 800;

interface Props {
  steps: RenewalSetupStep[];
  currentStep: RenewalSetupStep;
  renewalSetupData?: Renewal;
  handleStepName: (stepName: RenewalSetupStep) => void;
}

const HealthPlansStep: React.FC<Props> = ({ steps, currentStep, renewalSetupData, handleStepName }) => {
  const [showNoteError, setShowNoteError] = useState(false);
  const healthPlansSelectedOption = useRenewalSetupStore((state) => state.healthPlans.selectedOption);
  const isOptionChosen = useRenewalSetupStore((state) => state.healthPlans.isOptionChosen);
  const setStepState = useRenewalSetupStore((state) => state.setStepState);
  const healthPlanDetailsNote = useRenewalSetupStore((state) => state.healthPlanDetailsNote);
  const setHealthPlanDetailsNote = useRenewalSetupStore((state) => state.setHealthPlanDetailsNote);

  const healthPlansRaw = useHealthPlansStore((state) => state.state.healthPlans);
  const healthPlans = useMemo(() => healthPlansRaw || [], [healthPlansRaw]);

  const planYearDatesFormatted = useMemo(() => {
    const from = renewalSetupData?.newPlanYear?.validFrom;
    const to = renewalSetupData?.newPlanYear?.validTo;
    return `${from ? dayjs(from).format(DEFAULT_DATE_FORMAT) : ''} – ${to ? dayjs(to).format(DEFAULT_DATE_FORMAT) : ''}`;
  }, [renewalSetupData?.newPlanYear?.validFrom, renewalSetupData?.newPlanYear?.validTo]);
  const errors = useHealthPlansStore((state) => state.state.errors);
  const setState = useHealthPlansStore((state) => state.setState);
  const setIsRevalidationPending = useRenewalSetupStore((state) => state.setIsRevalidationPending);
  const serverInvalidCodes = useRenewalSetupStore(
    (state) => state.serverInvalidCodes[RenewalSetupStep.HealthPlans]
  );

  const { runActiveValidation, isValidating, showPlansError, setShowPlansError } = useRunActiveCodeValidation(
    {
      step: RenewalSetupStep.HealthPlans,
      orgId: renewalSetupData?.orgId,
      getHasClientErrors: () => hasAnyFieldError(errors),
      getPlanCodes: () => healthPlans.map((hp) => hp.planCode).filter(Boolean) as string[],
    }
  );

  const healthPlansCodesKey = healthPlans.map((hp) => hp.planCode).join('|');
  const debouncedCodesKey = useDebounce(healthPlansCodesKey);

  useEffect(() => {
    if (!isOptionChosen || healthPlansSelectedOption !== RenewalPlanOptionValues.ACTIVE) return;
    setIsRevalidationPending(true);
  }, [healthPlansCodesKey, isOptionChosen, healthPlansSelectedOption, setIsRevalidationPending]);

  useEffect(() => {
    if (!isOptionChosen || healthPlansSelectedOption !== RenewalPlanOptionValues.ACTIVE) return;
    runActiveValidation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedCodesKey, isOptionChosen, healthPlansSelectedOption]);

  const handleChooseOption = useCallback(() => {
    setStepState(RenewalSetupStep.HealthPlans, { isOptionChosen: true });
    // For ACTIVE validate plan codes right after confirming the option.
    if (healthPlansSelectedOption === RenewalPlanOptionValues.ACTIVE) {
      runActiveValidation();
    }
  }, [healthPlansSelectedOption, runActiveValidation, setStepState]);

  const handleOptionChange = useCallback(
    (option: StepOption) => {
      setStepState(RenewalSetupStep.HealthPlans, { selectedOption: option });
      if (!isOptionChosen) return;

      if (option === RenewalPlanOptionValues.ACTIVE) {
        runActiveValidation();
      } else {
        setShowPlansError(false);
        setStepState(RenewalSetupStep.HealthPlans, { isFailed: false });
      }
    },
    [isOptionChosen, runActiveValidation, setShowPlansError, setStepState]
  );

  const handleNoteChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLTextAreaElement>) => {
      const slicedValue = e.target.value.slice(0, MAX_NOTE_LENGTH);
      setHealthPlanDetailsNote(slicedValue);
      if (showNoteError) setShowNoteError(false);
    },
    [setHealthPlanDetailsNote, showNoteError]
  );

  const noteErrorText = useMemo(
    () => (showNoteError && !healthPlanDetailsNote ? REQUIRED_TEXT : undefined),
    [healthPlanDetailsNote, showNoteError]
  );

  const handleSubmit = useCallback(async () => {
    if (
      (healthPlansSelectedOption === RenewalPlanOptionValues.REQUESTED ||
        healthPlansSelectedOption === RenewalPlanOptionValues.CANCELED) &&
      !healthPlanDetailsNote
    ) {
      setShowNoteError(true);
      return;
    }

    const nextStep = steps[steps.indexOf(currentStep) + 1];

    // For UPDATE_TIERS (ACTIVE): validate plan codes before proceeding.
    if (healthPlansSelectedOption === RenewalPlanOptionValues.ACTIVE) {
      const isValid = await runActiveValidation();
      if (!isValid) {
        return;
      }
    }

    setStepState(RenewalSetupStep.HealthPlans, { isSubmitted: true });
    handleStepName(nextStep);
  }, [
    currentStep,
    handleStepName,
    healthPlanDetailsNote,
    healthPlansSelectedOption,
    runActiveValidation,
    setStepState,
    steps,
  ]);

  useEffect(() => {
    if (!healthPlans.length) return;
    setState((prev) => ({ ...prev, errors: validateHealthPlans(healthPlans, serverInvalidCodes || []) }));
  }, [healthPlans, serverInvalidCodes, setState]);

  return (
    <Box style={{ maxWidth: RENEWAL_SETUP_CONTENT_WIDTH }} data-testid="health-plans-step">
      <Box margin={{ bottom: 'xs' }}>
        <CarrierConfigIcon size="48px" color={theme.colors.cardTypes[CardTypesEnum.RX]} />
      </Box>
      <Inscription weight="bold" size="32px" lineHeight="44px" color="textTitle" margin={{ bottom: 'xs' }}>
        Renew Health Plans?
      </Inscription>
      <Text color="textBody" margin={{ bottom: 'l' }}>
        Your current plan year ends on{' '}
        <Text as="span" weight={700}>
          {dayjs(renewalSetupData?.planYear?.validTo).format(DEFAULT_DATE_FORMAT)}
        </Text>
        . Renewing plans will be from{' '}
        <Text as="span" weight={700}>
          {dayjs(renewalSetupData?.newPlanYear?.validFrom).format(DEFAULT_DATE_FORMAT)}
        </Text>{' '}
        to{' '}
        <Text as="span" weight={700}>
          {dayjs(renewalSetupData?.newPlanYear?.validTo).format(DEFAULT_DATE_FORMAT)}
        </Text>
        .
      </Text>
      <HealthPlansSummary renewalSetupData={renewalSetupData} />
      <HealthPlansForm
        healthPlansSelectedOption={healthPlansSelectedOption}
        onOptionChange={handleOptionChange}
      />
      {!isOptionChosen && (
        <AppButton
          disabled={!healthPlansSelectedOption}
          onClick={handleChooseOption}
          testId="health-plans-choose-option-button"
          style={{ marginTop: theme.spacings.l, height: '54px' }}
          containerStyle={{ maxWidth: 'none', width: '100%' }}
        >
          Choose Option
        </AppButton>
      )}
      {isOptionChosen && (
        <>
          <Box margin={{ vertical: 'l' }}>
            <DividerIcon width="100%" />
          </Box>
          {healthPlansSelectedOption === RenewalPlanOptionValues.ACTIVE && (
            <>
              <Inscription
                weight="bold"
                size="32px"
                lineHeight="44px"
                color="textTitle"
                margin={{ bottom: 'l' }}
              >
                Summary of Your Health Plans
              </Inscription>
              <Box background="module" pad="l" round="container1Round">
                <Box direction="row" align="center" justify="between" margin={{ bottom: 'spacing8' }}>
                  <Text color="textBody">First and last date of plan year:</Text>
                  <Text size="small" color="textBody" weight={700}>
                    {dayjs(renewalSetupData?.newPlanYear?.validFrom).format(DEFAULT_DATE_FORMAT)} –{' '}
                    {dayjs(renewalSetupData?.newPlanYear?.validTo).format(DEFAULT_DATE_FORMAT)}
                  </Text>
                </Box>
                <Box gap="spacing12">
                  {healthPlans.map((healthPlan, index) => {
                    const originalHealthPlan =
                      (renewalSetupData?.healthPlans || []).find((hp) => hp.id === healthPlan.id) ||
                      (renewalSetupData?.healthPlans || [])[index];
                    return (
                      <HealthPlanSummaryCard
                        key={healthPlan.id || `${healthPlan.name}-${index}`}
                        healthPlan={healthPlan}
                        testId={`health-plan-edit-card-${index}`}
                        showEditButtons
                        showError={showPlansError}
                        planYearDates={planYearDatesFormatted}
                        originalDeductibleConfigs={originalHealthPlan?.deductibleConfigs}
                      />
                    );
                  })}
                </Box>
              </Box>
            </>
          )}
          {healthPlansSelectedOption === RenewalPlanOptionValues.REQUESTED && (
            <>
              <Inscription
                weight="bold"
                size="32px"
                lineHeight="44px"
                color="textTitle"
                margin={{ bottom: 'xs' }}
              >
                Renew Your Health Plans
              </Inscription>
              <Inscription lineHeight="20px" color="textBody" margin={{ bottom: 'l' }}>
                Your health plan renewal options are managed by{' '}
                <Inscription weight="bold">{renewalSetupData?.partnerName}</Inscription>. Please specify any
                changes you would like to see in your upcoming health plan and we will be in touch with you.
              </Inscription>
              <Box background="module" pad="l" round="container1Round">
                <Box
                  pad="l"
                  background="canvas"
                  round="container1Round"
                  data-testid="health-plan-details-note-wrapper"
                  border={{ color: 'border1' }}
                >
                  <Box direction="row" margin={{ bottom: 'xs' }}>
                    <Text color="danger" weight="bold" size="medium" margin={{ right: 'xxsmall' }}>
                      *
                    </Text>
                    <Inscription weight="bold" lineHeight="20px" color="textBody">
                      Renewal details
                    </Inscription>
                  </Box>
                  <TextAreaInput
                    placeholder="Specify what changes you would like to make to your health plans"
                    value={healthPlanDetailsNote}
                    onChange={handleNoteChange}
                    style={{
                      minHeight: '240px',
                      borderColor: noteErrorText ? theme.colors.danger : undefined,
                      borderWidth: noteErrorText ? '2px' : undefined,
                    }}
                  />
                  {noteErrorText && (
                    <Box margin={{ top: 'spacing8' }}>
                      <ErrorMessage errorText={noteErrorText} errorStyle={{ fontWeight: 500 }} />
                    </Box>
                  )}
                </Box>
              </Box>
            </>
          )}
          {healthPlansSelectedOption === RenewalPlanOptionValues.CANCELED && (
            <>
              <Inscription
                weight="bold"
                size="32px"
                lineHeight="44px"
                color="textTitle"
                margin={{ bottom: 'l' }}
              >
                Do not set up any health plans for the upcoming plan year?
              </Inscription>
              <Box background="module" pad="l" round="container1Round">
                <Box
                  pad="l"
                  background="canvas"
                  round="container1Round"
                  data-testid="health-plan-do-not-renew-note-wrapper"
                  border={{ color: 'border1' }}
                >
                  <Box direction="row" margin={{ bottom: 'xs' }}>
                    <Text color="danger" weight="bold" size="medium" margin={{ right: 'xxsmall' }}>
                      *
                    </Text>
                    <Inscription weight="bold" lineHeight="20px" color="textBody">
                      Reason for not offering health plan deductible tracking in the new year
                    </Inscription>
                  </Box>
                  <TextAreaInput
                    placeholder="Specify your reason"
                    value={healthPlanDetailsNote}
                    onChange={handleNoteChange}
                    style={{
                      minHeight: '240px',
                      borderColor: noteErrorText ? theme.colors.danger : undefined,
                      borderWidth: noteErrorText ? '2px' : undefined,
                    }}
                  />
                  {noteErrorText && (
                    <Box margin={{ top: 'spacing8' }}>
                      <ErrorMessage errorText={noteErrorText} errorStyle={{ fontWeight: 500 }} />
                    </Box>
                  )}
                </Box>
              </Box>
            </>
          )}
          <Inscription color="textBody" weight="bold" textAlign="center" margin={{ top: 'l', bottom: 's' }}>
            Look good? If so, proceed to the next step!
          </Inscription>
          <AppButton
            onClick={handleSubmit}
            disabled={isValidating}
            testId="submit-health-plans-renew"
            style={{ height: '54px' }}
            containerStyle={{ maxWidth: 'none', width: '100%' }}
          >
            {isValidating ? <Preloader /> : getStepLabel(steps, currentStep)}
          </AppButton>
        </>
      )}
      <RenewalSetupLinks renewalSetupData={renewalSetupData} />
    </Box>
  );
};

export default HealthPlansStep;
